import { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { useActivePerDay, useAlertDayDetail, useAlertPipeline } from '../hooks/useAlertOverview.ts';
import type {
  ActiveAlertsPerDayDataPoint,
  AlertRange,
  CategoryMeterSizeBreakdown,
} from '@metron/shared';
import { Legend } from 'recharts';
import { toErrorMessage } from '@metron/shared';

const TREND_SERIES = {
  ended: { label: 'Ended', color: '#10b981' },
  detected: { label: 'Detected', color: '#3b82f6' },
  ongoing: { label: 'Ongoing', color: '#f59e0b' },
};

// Types for Recharts tooltip
interface TooltipPayload {
  dataKey: string;
  color: string;
  value: number;
  payload: Record<string, unknown>;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

function TrendTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;

  const rawDay = payload[0]?.payload?.day as string | undefined;
  const date = rawDay ? new Date(rawDay) : null;
  const formattedDate = date
    ? date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
    })
    : String(label);

  return (
    <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
      <p className='font-semibold mb-2'>{formattedDate}</p>
      <div className='space-y-1'>
        {[...payload].reverse().map((entry: TooltipPayload) => {
          const key = entry.dataKey as keyof typeof TREND_SERIES;
          const seriesInfo = TREND_SERIES[key];

          return (
            <div key={key} className='flex items-center gap-2'>
              <div
                className='w-3 h-3 rounded-sm'
                style={{ backgroundColor: entry.color }}
              />
              <span className='text-sm'>
                {seriesInfo?.label || key}: <span className='font-semibold'>{entry.value}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CATEGORY_COLORS: Record<string, string> = {
  Leak: '#f85151',
  'High use': '#a62ed2',
  'Missed read': '#f79149',
  'Zero use': '#507cd4',
  Backflow: '#f5c858',
  'Low temperature': '#06b6d4',
};

function getCategoryColor(name: string): string {
  return CATEGORY_COLORS[name] ?? '#8b5cf6';
}

const SUBTYPE_COLORS: Record<string, string> = {
  'Constant use': '#f85151',
  'Anomaly': '#f97316',
  'High use': '#a62ed2',
  'Emergency': '#7c3aed',
  'Backflow': '#f5c858',
  'Net negative flow': '#e0a030',
  'Water': '#06b6d4',
  'Air': '#0ea5e9',
  'Anomaly off': '#507cd4',
};

function getSubtypeColor(name: string, categoryDisplayName?: string): string {
  return SUBTYPE_COLORS[name] ??
    (categoryDisplayName ? getCategoryColor(categoryDisplayName) : '#8b5cf6');
}

function DailyBarChart(
  { chartData, categories, height, selectedDay, onDayClick, emptyMessage, showXAxis = true }: {
    chartData: Record<string, unknown>[];
    categories: string[];
    height: number;
    selectedDay: string | null;
    onDayClick: (day: string) => void;
    emptyMessage: string;
    showXAxis?: boolean;
  },
) {
  if (!chartData.length || !chartData.some((d) => (d.total as number) > 0)) {
    return (
      <div style={{ height }} className='flex items-center justify-center'>
        <p className='text-muted-foreground'>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer
      width='100%'
      height={height}
      className='[&_svg]:outline-none [&_*]:outline-none cursor-pointer'
    >
      <BarChart
        data={chartData}
        onClick={(state: unknown) => {
          const s = state as { activeTooltipIndex?: string };
          const idx = s?.activeTooltipIndex != null ? parseInt(s.activeTooltipIndex, 10) : -1;
          const day = chartData[idx]?.day as string;
          if (day) onDayClick(day);
        }}
      >
        <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
        <XAxis dataKey='dayLabel' tick={{ fontSize: 12 }} hide={!showXAxis} />
        <YAxis />
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload || !payload.length) return null;
            const d = payload[0].payload;
            const date = new Date(d.day + 'T00:00:00').toLocaleDateString([], {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
            });
            return (
              <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
                <p className='font-semibold mb-2'>{date}</p>
                <div className='space-y-1'>
                  {payload.map((
                    entry: { dataKey: string; color: string; value: number },
                  ) => (
                    <div key={entry.dataKey} className='flex items-center gap-2'>
                      <div
                        className='w-3 h-3 rounded-sm'
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className='text-sm'>
                        {entry.dataKey}:{' '}
                        <span className='font-semibold'>
                          {entry.value.toLocaleString()}
                        </span>
                      </span>
                    </div>
                  ))}
                  <div className='border-t pt-1 mt-1'>
                    <span className='text-sm text-muted-foreground'>
                      Total:{' '}
                      <span className='font-semibold'>
                        {(d.total as number).toLocaleString()}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            );
          }}
        />
        <Legend />
        {categories.map((cat) => (
          <Bar
            key={cat}
            dataKey={cat}
            stackId='category'
            fill={getCategoryColor(cat)}
            name={cat}
          >
            {chartData.map((entry, idx) => (
              <Cell
                key={idx}
                opacity={entry.day === selectedDay ? 1 : 0.3}
              />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function AlertsPerDayCard(
  { data, isLoading, selectedDay, onDayClick }: {
    data: ActiveAlertsPerDayDataPoint[];
    isLoading: boolean;
    selectedDay: string | null;
    onDayClick: (day: string) => void;
  },
) {
  const activeCategories = new Set<string>();
  const activeChartData = data.map((item) => {
    const flat: Record<string, unknown> = {
      day: item.day,
      dayLabel: new Date(item.day + 'T00:00:00').toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
      }),
      total: item.total,
    };
    for (const [cat, count] of Object.entries(item.byCategory)) {
      flat[cat] = count;
      activeCategories.add(cat);
    }
    return flat;
  });

  const endedCategories = new Set<string>();
  const endedChartData = data.map((item) => {
    const flat: Record<string, unknown> = {
      day: item.day,
      dayLabel: new Date(item.day + 'T00:00:00').toLocaleDateString([], {
        month: 'short',
        day: 'numeric',
      }),
      total: item.totalEnded,
    };
    for (const [cat, count] of Object.entries(item.endedByCategory)) {
      flat[cat] = count;
      endedCategories.add(cat);
    }
    return flat;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Alerts Per Day</CardTitle>
        <CardDescription>
          Click a bar to select a day and see breakdown details below. Days are midnight to midnight
          UTC.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading
          ? (
            <div className='h-[500px] flex items-center justify-center'>
              <div className='flex flex-col items-center gap-2'>
                <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                <p className='text-muted-foreground'>Loading chart data...</p>
              </div>
            </div>
          )
          : (
            <div className='space-y-4'>
              <div>
                <h4 className='text-sm font-medium text-muted-foreground mb-2'>
                  Active alerts which were ongoing at end of each day
                </h4>
                <DailyBarChart
                  chartData={activeChartData}
                  categories={[...activeCategories].sort()}
                  height={225}
                  selectedDay={selectedDay}
                  onDayClick={onDayClick}
                  emptyMessage='No active alerts data available.'
                  showXAxis={false}
                />
              </div>
              <div>
                <h4 className='text-sm font-medium text-muted-foreground mb-2'>
                  Active alerts which ended before the end of each day
                </h4>
                <DailyBarChart
                  chartData={endedChartData}
                  categories={[...endedCategories].sort()}
                  height={225}
                  selectedDay={selectedDay}
                  onDayClick={onDayClick}
                  emptyMessage='No ended alerts in this period.'
                />
              </div>
            </div>
          )}
      </CardContent>
    </Card>
  );
}

function MeterListModal({
  open,
  onClose,
  title,
  ranges,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  ranges: AlertRange[];
}) {
  const sorted = ranges.sort((a, b) => a.deviceId.localeCompare(b.deviceId));
  const maxRows = 100;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className='text-sm text-muted-foreground mb-3'>
          {sorted.length.toLocaleString()} meters
          {sorted.length > maxRows && ` (showing first ${maxRows})`}
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Device ID</TableHead>
              <TableHead>Subtype</TableHead>
              <TableHead>Meter Size</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.slice(0, maxRows).map((range, i) => (
              <TableRow key={`${range.sensorId}-${i}`}>
                <TableCell>
                  <Link
                    to={`/meter-inspector/${range.sensorId}`}
                    className='font-mono text-blue-600 hover:underline dark:text-blue-400'
                  >
                    {range.deviceId}
                  </Link>
                </TableCell>
                <TableCell>{range.subtypeDisplayName}</TableCell>
                <TableCell>{range.meterSize ? `${range.meterSize}"` : '—'}</TableCell>
                <TableCell>
                  <Badge variant='secondary'>{range.eventType}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDetailSection({
  categoryDisplayName,
  subtypes,
  alertRanges,
  meterSizeData,
  totalMetersBySize,
  selectedDay,
}: {
  categoryDisplayName: string;
  subtypes: Array<{
    subtypeDisplayName: string;
    activeCount: number;
    newCount: number;
    ongoingCount: number;
    endedCount: number;
  }>;
  alertRanges?: AlertRange[];
  meterSizeData?: CategoryMeterSizeBreakdown[];
  totalMetersBySize?: Map<string, number>;
  selectedDay?: string | null;
}) {
  const [meterListModal, setMeterListModal] = useState<
    { title: string; ranges: AlertRange[] } | null
  >(null);
  // Flatten meterSizeData for recharts — compute % of meters alerting by subtype
  const allSubtypes = new Set<string>();
  const meterSizeChartData = (meterSizeData ?? []).map((item) => {
    const totalMeters = totalMetersBySize?.get(item.meterSize) ?? 0;
    const flat: Record<string, unknown> = {
      meterSize: item.meterSize,
      totalMeters,
      alertingCount: item.activeCount,
      pct: totalMeters > 0 ? Math.round((item.activeCount / totalMeters) * 10000) / 100 : 0,
    };
    for (const [subtype, count] of Object.entries(item.bySubtype)) {
      flat[subtype] = totalMeters > 0 ? Math.round((count / totalMeters) * 10000) / 100 : 0;
      flat[`${subtype}_count`] = count;
      allSubtypes.add(subtype);
    }
    return flat;
  });
  const subtypeNames = [...allSubtypes].sort();

  return (
    <Card>
      <CardHeader>
        <div className='flex items-start justify-between'>
          <div>
            <CardTitle>{categoryDisplayName}</CardTitle>
            <CardDescription>
              Subcategory detail for {categoryDisplayName.toLowerCase()} alerts
            </CardDescription>
          </div>
          {selectedDay && (
            <div className='text-right shrink-0'>
              <span className='text-sm font-medium'>
                {new Date(selectedDay + 'T00:00:00').toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <p className='text-xs text-muted-foreground'>
                Click a bar in the overview chart to change the day
              </p>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {meterSizeData === undefined
          ? (
            <div className='h-[250px] flex items-center justify-center mb-6'>
              <div className='flex flex-col items-center gap-2'>
                <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                <p className='text-muted-foreground'>Loading meter size data...</p>
              </div>
            </div>
          )
          : meterSizeChartData.length === 0
          ? (
            <div className='h-[250px] flex items-center justify-center mb-6'>
              <p className='text-muted-foreground'>No meter size data available.</p>
            </div>
          )
          : (
            <div className='mb-6'>
              <h4 className='text-sm font-medium text-muted-foreground mb-2'>
                % of Meters Alerting by Size
              </h4>
              <ResponsiveContainer
                width='100%'
                height={250}
                className='[&_svg]:outline-none [&_*]:outline-none cursor-pointer'
              >
                <BarChart
                  data={meterSizeChartData}
                  onClick={(state: unknown) => {
                    const s = state as { activeTooltipIndex?: string };
                    const idx = s?.activeTooltipIndex != null
                      ? parseInt(s.activeTooltipIndex, 10)
                      : -1;
                    const d = meterSizeChartData[idx];
                    if (!d) return;
                    const meterSize = d.meterSize as string;
                    const filtered = (alertRanges ?? []).filter((r) =>
                      (r.meterSize ?? 'Unknown') === meterSize
                    );
                    const sizeLabel = meterSize === 'Unknown' ? 'Unknown size' : `${meterSize}"`;
                    setMeterListModal({
                      title: `${sizeLabel} meters — ${categoryDisplayName} alerts`,
                      ranges: filtered,
                    });
                  }}
                >
                  <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                  <XAxis dataKey='meterSize' tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v: number) => `${v}%`} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className='bg-background border border-border rounded-lg shadow-lg p-3'>
                          <p className='font-semibold mb-2'>
                            {d.meterSize === 'Unknown' ? 'Unknown size' : `${d.meterSize}" meter`}
                          </p>
                          <div className='space-y-1'>
                            {payload.map((
                              entry: { dataKey: string; color: string; value: number },
                            ) => {
                              const count = d[`${entry.dataKey}_count`] as number;
                              return (
                                <div key={entry.dataKey} className='flex items-center gap-2'>
                                  <div
                                    className='w-3 h-3 rounded-sm'
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className='text-sm'>
                                    {entry.dataKey}:{' '}
                                    <span className='font-semibold'>
                                      {entry.value.toFixed(1)}%
                                    </span>{' '}
                                    <span className='text-muted-foreground'>
                                      ({count?.toLocaleString()} of{' '}
                                      {(d.totalMeters as number).toLocaleString()})
                                    </span>
                                  </span>
                                </div>
                              );
                            })}
                            <div className='border-t pt-1 mt-1'>
                              <span className='text-sm text-muted-foreground'>
                                Total:{' '}
                                <span className='font-semibold'>
                                  {(d.pct as number).toFixed(1)}%
                                </span>{' '}
                                ({(d.alertingCount as number).toLocaleString()} of{' '}
                                {(d.totalMeters as number).toLocaleString()} meters)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  {subtypeNames.map((subtype) => (
                    <Bar
                      key={subtype}
                      dataKey={subtype}
                      stackId='subtype'
                      fill={getSubtypeColor(subtype, categoryDisplayName)}
                      name={subtype}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        <MeterListModal
          open={meterListModal !== null}
          onClose={() => setMeterListModal(null)}
          title={meterListModal?.title ?? ''}
          ranges={meterListModal?.ranges ?? []}
        />
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-full'>Subtype</TableHead>
              <TableHead className='whitespace-nowrap'>Active</TableHead>
              <TableHead className='text-right whitespace-nowrap'>Ended</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subtypes.length === 0
              ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    className='text-center text-muted-foreground'
                  >
                    No subcategory data available.
                  </TableCell>
                </TableRow>
              )
              : subtypes.map((st) => (
                <TableRow
                  key={st.subtypeDisplayName}
                  className='cursor-pointer'
                  onClick={() => {
                    const filtered = (alertRanges ?? []).filter((r) =>
                      r.subtypeDisplayName === st.subtypeDisplayName
                    );
                    setMeterListModal({
                      title: `${st.subtypeDisplayName} — ${categoryDisplayName} alerts`,
                      ranges: filtered,
                    });
                  }}
                >
                  <TableCell className='font-medium'>
                    {st.subtypeDisplayName}
                  </TableCell>
                  <TableCell>
                    <span className='inline-flex items-center gap-2'>
                      <Badge className='bg-blue-600 hover:bg-blue-700 tabular-nums'>
                        {st.activeCount.toLocaleString()}
                      </Badge>
                      {st.activeCount > 0 && (
                        <span className='text-xs text-muted-foreground tabular-nums whitespace-nowrap'>
                          ({st.newCount.toLocaleString()} new, {st.ongoingCount.toLocaleString()}
                          {' '}
                          ongoing)
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className='text-right'>
                    <Badge className='bg-emerald-600 hover:bg-emerald-700'>
                      {st.endedCount.toLocaleString()}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function AlertOverviewPage() {
  const [days, setDays] = useState(7);
  const [selectedDay, setSelectedDay] = useState(
    new Date().toISOString().split('T')[0],
  );

  // Three independent data sources — load in parallel, render progressively
  const { data: chartData, isLoading: isChartLoading, error } = useActivePerDay(days);
  const { data: dayDetailData, isLoading: isDayDetailLoading } = useAlertDayDetail(selectedDay);
  const { data: pipelineData, isLoading: isPipelineLoading } = useAlertPipeline(days);

  const trendData = pipelineData?.trend.map((point) => {
    const date = new Date(point.day);
    return {
      ...point,
      dayLabel: date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
      }),
    };
  }) ?? [];

  // Build totalMetersBySize lookup for % of meters alerting charts
  const totalMetersBySize = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of chartData?.meterSizeAlertRate ?? []) {
      map.set(r.meterSize, r.totalMeters);
    }
    return map;
  }, [chartData?.meterSizeAlertRate]);

  const dayData = useMemo(() => {
    if (!selectedDay || !dayDetailData?.alertRanges) return null;

    const allRanges = dayDetailData.alertRanges;
    const isActive = (r: { eventType: string }) =>
      r.eventType !== 'ended' && r.eventType !== 'cleared';

    // Group by category -> subtypes with active + ended counts
    const byCat = new Map<
      string,
      {
        categoryDisplayName: string;
        subtypes: Array<{
          subtypeDisplayName: string;
          activeCount: number;
          newCount: number;
          ongoingCount: number;
          endedCount: number;
        }>;
      }
    >();
    const subCounts = new Map<
      string,
      {
        cat: string;
        catDisplay: string;
        subDisplay: string;
        active: number;
        new_: number;
        ongoing: number;
        ended: number;
      }
    >();
    for (const r of allRanges) {
      const key = `${r.category}:${r.subtype}`;
      const e = subCounts.get(key);
      const active = isActive(r);
      const isNew = r.eventType === 'detected';
      const isOngoing = active && !isNew;
      if (e) {
        if (active) e.active++;
        else e.ended++;
        if (isNew) e.new_++;
        if (isOngoing) e.ongoing++;
      } else {
        subCounts.set(key, {
          cat: r.category,
          catDisplay: r.categoryDisplayName,
          subDisplay: r.subtypeDisplayName,
          active: active ? 1 : 0,
          new_: isNew ? 1 : 0,
          ongoing: isOngoing ? 1 : 0,
          ended: active ? 0 : 1,
        });
      }
    }
    for (const [, s] of subCounts) {
      let cat = byCat.get(s.cat);
      if (!cat) {
        cat = { categoryDisplayName: s.catDisplay, subtypes: [] };
        byCat.set(s.cat, cat);
      }
      cat.subtypes.push({
        subtypeDisplayName: s.subDisplay,
        activeCount: s.active,
        newCount: s.new_,
        ongoingCount: s.ongoing,
        endedCount: s.ended,
      });
    }

    // Per-category meter size breakdown
    const catMeterSize: Record<string, CategoryMeterSizeBreakdown[]> = {};
    const catMeterMap = new Map<
      string,
      Map<string, { total: number; bySubtype: Map<string, number> }>
    >();
    for (const r of allRanges) {
      const sizeKey = r.meterSize ?? 'Unknown';
      if (!catMeterMap.has(r.category)) catMeterMap.set(r.category, new Map());
      const sizeMap = catMeterMap.get(r.category)!;
      const entry = sizeMap.get(sizeKey);
      if (entry) {
        entry.total++;
        entry.bySubtype.set(
          r.subtypeDisplayName,
          (entry.bySubtype.get(r.subtypeDisplayName) ?? 0) + 1,
        );
      } else {
        sizeMap.set(sizeKey, {
          total: 1,
          bySubtype: new Map([[r.subtypeDisplayName, 1]]),
        });
      }
    }
    for (const [cat, sizeMap] of catMeterMap) {
      catMeterSize[cat] = [...sizeMap.entries()].map(([meterSize, { total, bySubtype }]) => ({
        meterSize,
        activeCount: total,
        bySubtype: Object.fromEntries(bySubtype),
      }));
    }

    // Sort meter sizes using the server-provided order as reference
    const sizeOrder = new Map(
      (chartData?.meterSizeAlertRate ?? []).map((r, i) => [r.meterSize, i]),
    );
    for (const cat in catMeterSize) {
      catMeterSize[cat].sort((a, b) =>
        (sizeOrder.get(a.meterSize) ?? 999) - (sizeOrder.get(b.meterSize) ?? 999)
      );
    }

    return { byCat, catMeterSize, allRanges };
  }, [selectedDay, dayDetailData, chartData?.meterSizeAlertRate]);

  if (error) {
    return (
      <div className='container mx-auto px-4 py-8'>
        <h1 className='text-3xl font-bold mb-6'>Alerts Overview</h1>
        <Card>
          <CardContent className='py-8'>
            <p className='text-red-500'>
              Error: {toErrorMessage(error, 'Failed to fetch alert overview')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Alerts Overview</h1>
          <p className='text-muted-foreground'>Monitor active alerts across all sensors</p>
        </div>
        <Select value={days.toString()} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className='w-[130px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='7'>Last 7 days</SelectItem>
            <SelectItem value='14'>Last 14 days</SelectItem>
            <SelectItem value='30'>Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <AlertsPerDayCard
        data={chartData?.activeAlertsPerDay ?? []}
        isLoading={isChartLoading}
        selectedDay={selectedDay}
        onDayClick={setSelectedDay}
      />

      {selectedDay && (
        <div className='flex items-center gap-3 pt-2'>
          <h2 className='text-lg font-semibold'>
            Alerts breakdown for{' '}
            {new Date(selectedDay + 'T00:00:00').toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </h2>
          <span className='text-sm text-muted-foreground'>
            Click a bar in the overview chart to change the day
          </span>
        </div>
      )}

      {/* Category Detail Sections — always driven by day-detail data */}
      {isDayDetailLoading
        ? (
          <Card>
            <CardContent className='py-8'>
              <div className='flex flex-col items-center gap-2'>
                <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                <p className='text-muted-foreground'>Loading category details...</p>
              </div>
            </CardContent>
          </Card>
        )
        : dayData
        ? [...dayData.byCat.entries()].map(
          ([category, { categoryDisplayName, subtypes: daySubs }]) => (
            <CategoryDetailSection
              key={category}
              categoryDisplayName={categoryDisplayName}
              subtypes={daySubs.map((s) => ({
                subtypeDisplayName: s.subtypeDisplayName,
                activeCount: s.activeCount,
                newCount: s.newCount,
                ongoingCount: s.ongoingCount,
                endedCount: s.endedCount,
              }))}
              alertRanges={dayData.allRanges.filter((r) => r.category === category)}
              meterSizeData={dayData.catMeterSize[category] ?? []}
              totalMetersBySize={totalMetersBySize}
              selectedDay={selectedDay}
            />
          ),
        )
        : null}

      {/* Event Pipeline Overview Section */}
      <div className='pt-8 border-t'>
        <h2 className='text-2xl font-bold'>Event Pipeline Overview</h2>
        <p className='text-muted-foreground mt-1'>
          Alert event processing activity and distribution
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Alert Events Emitted</CardTitle>
          <CardDescription>
            Daily alert activity over the last {days} days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPipelineLoading
            ? (
              <div className='h-[350px] flex items-center justify-center'>
                <div className='flex flex-col items-center gap-2'>
                  <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                  <p className='text-muted-foreground'>Loading chart data...</p>
                </div>
              </div>
            )
            : !trendData?.length
            ? (
              <div className='h-[350px] flex items-center justify-center'>
                <p className='text-muted-foreground'>No trend data available for this period.</p>
              </div>
            )
            : (
              <ResponsiveContainer
                width='100%'
                height={350}
                className='[&_svg]:outline-none [&_*]:outline-none'
              >
                <AreaChart data={trendData}>
                  <CartesianGrid vertical={false} stroke='hsl(var(--border))' />
                  <XAxis dataKey='dayLabel' tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip
                    content={<TrendTooltip />}
                    labelFormatter={(label) => {
                      const point = trendData.find((d) => d.dayLabel === label);
                      return point?.day ?? label;
                    }}
                  />
                  {Object.entries(TREND_SERIES).map(([key, { label, color }]) => (
                    <Area
                      key={key}
                      type='monotone'
                      dataKey={key}
                      stackId='trend'
                      stroke={color}
                      fill={color}
                      fillOpacity={0.3}
                      dot={{ r: 3, fill: color }}
                      name={label}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
        </CardContent>
      </Card>

      {/* Sensors with the most events */}
      <Card>
        <CardHeader>
          <CardTitle>Sensors with the Most Events</CardTitle>
          <CardDescription>
            Sensors with the most alert events emitted
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isPipelineLoading
            ? (
              <div className='h-[300px] flex items-center justify-center'>
                <div className='flex flex-col items-center gap-2'>
                  <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
                  <p className='text-muted-foreground'>Loading sensor data...</p>
                </div>
              </div>
            )
            : !pipelineData?.topEventSensors?.length
            ? (
              <div className='h-[300px] flex items-center justify-center'>
                <p className='text-muted-foreground'>No sensor data available.</p>
              </div>
            )
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device ID</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead className='text-right'>Events</TableHead>
                    <TableHead>Categories</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pipelineData.topEventSensors.map((sensor) => (
                    <TableRow key={sensor.sensorId}>
                      <TableCell>
                        <Link
                          to={`/meter-inspector/${sensor.sensorId}`}
                          className='font-mono text-blue-600 hover:underline dark:text-blue-400'
                        >
                          {sensor.deviceId}
                        </Link>
                      </TableCell>
                      <TableCell className='text-muted-foreground'>
                        {sensor.meterSize ? `${sensor.meterSize}"` : '—'}
                      </TableCell>
                      <TableCell className='text-right font-semibold'>
                        {sensor.eventCount}
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-wrap gap-1'>
                          {sensor.categories.map((cat) => (
                            <Badge key={cat} variant='secondary'>
                              {cat}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
